import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { NEET_PHYSICS_CH4_QUESTIONS } from "../data/neetPhysicsCh4Questions";

// ─────────────────────────────────────────────────────────────────────────────
// NEET Physics — Chapter 4: Newton's Laws of Motion (NCERT Class 11, "Laws of
// Motion"). Content is drafted from the NCERT syllabus topic list; each concept
// has an explanation plus a worked example. NOT YET human-reviewed by a
// subject-matter expert — treat as a first draft and verify against NCERT
// before exam use.
// ─────────────────────────────────────────────────────────────────────────────

const SECTIONS = ["Newton's Three Laws", "Momentum & Conservation", "Friction", "Circular Motion Dynamics"];

const SECTION_COLORS = {
  "Newton's Three Laws":       "#2563eb",
  "Momentum & Conservation":   "#7c3aed",
  "Friction":                  "#ea580c",
  "Circular Motion Dynamics":  "#0d9488",
};

const CONCEPTS = [
  // ── Newton's Three Laws ─────────────────────────────────────────────────────
  { section: "Newton's Three Laws", title: "Newton's First Law of Motion (Law of Inertia)",
    explanation: "Newton's first law states that a body continues in its state of rest, or of uniform motion in a straight line, unless it is acted upon by a net (unbalanced) external force. This property of matter to resist any change in its state of rest or motion is called inertia, and mass is the quantitative measure of a body's inertia -- the more massive a body, the greater its inertia. The first law also serves to define what a 'force' is: something that changes a body's velocity.",
    example: `Passenger standing in a moving bus:
Bus suddenly brakes -> lower body (in contact with floor)
decelerates with the bus, but the upper body tends to keep
moving forward at the original speed (inertia of motion)
-> passenger lurches FORWARD

Bus suddenly accelerates from rest -> passenger's body lags
behind (inertia of rest) -> passenger lurches BACKWARD` },
  { section: "Newton's Three Laws", title: "Newton's Second Law — F = ma and F = dp/dt",
    explanation: "Newton's second law states that the net force on a body equals the time rate of change of its linear momentum, F = dp/dt, and this force acts in the direction of that change. For a body of constant mass, p = mv, so F = m(dv/dt) = ma -- the familiar F = ma is a special case of the more general momentum form, which also applies when mass itself varies (e.g. a rocket burning fuel).",
    example: `General form:      F = dp/dt = d(mv)/dt
Constant mass m:    F = m(dv/dt) = ma

A 4 kg block accelerates at 5 m/s^2 under a net force F:
F = ma = 4 kg x 5 m/s^2 = 20 N` },
  { section: "Newton's Three Laws", title: "Newton's Third Law — Action-Reaction Pairs",
    explanation: "For every action (force exerted by body A on body B), there is a reaction (force exerted by body B on body A) that is equal in magnitude, opposite in direction, and of the same nature, acting simultaneously. Crucially, action and reaction act on two different bodies, so they never cancel each other out -- they only appear to 'cancel' when analysing the net force on a single body if you mistakenly include both forces in the same free body diagram.",
    example: `Book resting on a table:
Book pushes DOWN on table with force N  (action, on the table)
Table pushes UP on book with force N    (reaction, on the book)
-- equal magnitude, opposite direction, act on DIFFERENT objects

(The book's weight mg and the normal reaction N on the book are
NOT an action-reaction pair -- they act on the SAME body, the book)` },
  { section: "Newton's Three Laws", title: "Common Forces — Weight, Normal Reaction, Tension, Applied Force",
    explanation: "Four everyday contact/field forces recur in mechanics problems: WEIGHT (W = mg), the pull of gravity acting vertically downward through the body's centre of mass; NORMAL REACTION (N), the contact force a surface exerts perpendicular to itself, preventing bodies from passing through each other; TENSION (T), the pulling force transmitted along a stretched string/rope/cable, directed away from the object along the string -- for an ideal (massless, inextensible) string over a frictionless pulley, tension is the same throughout; and APPLIED FORCE (F), any push or pull exerted directly by an external agent.",
    example: `Block hanging at rest from a single ideal string:
Only two forces act: Weight (mg, down) and Tension (T, up)
Equilibrium -> T = mg

Block resting on a horizontal table:
Weight (mg, down) and Normal reaction (N, up)
Equilibrium -> N = mg` },
  { section: "Newton's Three Laws", title: "Free Body Diagrams and Equilibrium of Concurrent Forces",
    explanation: "A free body diagram (FBD) isolates one object and shows every external force acting ON it (never the forces it exerts on other bodies). A body is in equilibrium under several concurrent (same point of application) forces when their vector sum is zero: sigma Fx = 0 and sigma Fy = 0. For exactly three concurrent coplanar forces P, Q, R keeping a body in equilibrium, Lami's theorem gives a quick alternative: each force is proportional to the sine of the angle between the other two.",
    example: `Lami's theorem (3 concurrent forces P, Q, R in equilibrium),
with alpha, beta, gamma the angles opposite P, Q, R respectively:

  P / sin(alpha) = Q / sin(beta) = R / sin(gamma)

Component form (any number of concurrent forces):
  sigma Fx = 0   and   sigma Fy = 0` },

  // ── Momentum & Conservation ──────────────────────────────────────────────────
  { section: "Momentum & Conservation", title: "Linear Momentum",
    explanation: "Linear momentum p of a body is the product of its mass and velocity, p = mv. It is a VECTOR quantity, pointing in the same direction as velocity, with SI unit kg.m/s (equivalently N.s). Momentum is a measure of the 'quantity of motion' a body carries, and is the quantity Newton's second and third laws are fundamentally built around.",
    example: `p = m v

A 0.5 kg ball moving at 20 m/s has momentum:
p = 0.5 kg x 20 m/s = 10 kg.m/s (in the direction of motion)` },
  { section: "Momentum & Conservation", title: "Impulse and the Impulse-Momentum Theorem",
    explanation: "Impulse J is the product of a (constant) force and the time for which it acts, J = F.delta_t, and equals the area under a force-time graph even when the force varies. The impulse-momentum theorem states that impulse equals the resulting change in momentum, J = delta_p = m.v_f - m.v_i -- this is just Newton's second law (F = dp/dt) integrated over time, and it's why a longer contact time (e.g. a cricketer 'giving' with the ball while catching it) reduces the peak force needed for a given change in momentum.",
    example: `J = F . delta_t = delta_p

A 50 N force acts on a 2 kg object for 0.2 s:
J = 50 N x 0.2 s = 10 N.s = 10 kg.m/s (change in momentum)` },
  { section: "Momentum & Conservation", title: "Conservation of Linear Momentum — The Principle",
    explanation: "For an isolated system (no net external force), the total linear momentum remains constant with time. This follows directly from Newton's second and third laws: for two interacting bodies, the force each exerts on the other is equal and opposite (third law), so by F = dp/dt each body's momentum changes at equal and opposite rates -- meaning the total momentum change of the system is zero.",
    example: `Two-body system, no external force:
F12 = -F21   (Newton's third law)
dp1/dt = -dp2/dt
=> d(p1 + p2)/dt = 0
=> p1 + p2 = constant   (total momentum conserved)

m1.u1 + m2.u2 = m1.v1 + m2.v2` },
  { section: "Momentum & Conservation", title: "Recoil of a Gun — Worked Example",
    explanation: "When a gun fires a bullet, the gun-bullet system starts at rest, so its total initial momentum is zero. Since the explosive force is purely internal (no net external horizontal force during firing), the total momentum must remain zero afterward -- so the bullet's forward momentum is exactly balanced by the gun's backward (recoil) momentum, m(bullet).v(bullet) = -M(gun).V(recoil).",
    example: `Bullet: mass m = 0.01 kg (10 g), muzzle speed v = 300 m/s
Gun:    mass M = 3 kg, recoil speed V = ?

m.v + M.V = 0
V = -(m.v)/M = -(0.01 x 300)/3 = -3/3 = -1 m/s

Gun recoils at 1 m/s, opposite to the bullet's direction` },
  { section: "Momentum & Conservation", title: "Momentum Conservation in Collisions",
    explanation: "In any collision (in the absence of external force), total linear momentum is ALWAYS conserved -- this holds whether the collision is elastic or inelastic. In an ELASTIC collision, kinetic energy is also conserved. In an INELASTIC collision, kinetic energy is NOT conserved (some is lost as heat, sound, or deformation) even though momentum still is; in a PERFECTLY INELASTIC collision the bodies stick together and move off with one common velocity.",
    example: `Perfectly inelastic collision: m1 at u1 hits stationary m2, they
stick together and move with common velocity v:

m1.u1 + m2.0 = (m1 + m2).v
v = m1.u1 / (m1 + m2)     <- momentum conserved, KE is NOT` },

  // ── Friction ──────────────────────────────────────────────────────────────
  { section: "Friction", title: "Static Friction vs Kinetic Friction",
    explanation: "Static friction acts between two surfaces that are NOT sliding relative to each other; it is 'self-adjusting', taking whatever value (up to a maximum, the limiting friction) is needed to prevent relative sliding. Kinetic (or sliding) friction acts once the surfaces ARE sliding, and is approximately independent of the relative speed for ordinary speeds. Generally, the maximum static friction is slightly greater than (or at least equal to) kinetic friction, which is why more force is often needed to START moving an object than to KEEP it moving.",
    example: `Applied force F increases from 0:
0 < F < f(s,max)  -> object stays still, static friction = F (matches F)
F = f(s,max)       -> object is on the verge of sliding
F > f(s,max)       -> object slides; kinetic friction f(k) now acts
                      (f(k) is roughly constant, and f(k) <= f(s,max))` },
  { section: "Friction", title: "Coefficient of Friction and Laws of Limiting Friction",
    explanation: "The coefficient of friction mu is the ratio of the (limiting/kinetic) friction force to the normal reaction, mu = f/N -- a dimensionless number depending on the two surfaces in contact. The laws of limiting friction state: (1) friction is directly proportional to the normal reaction, f = mu.N; (2) friction is independent of the apparent area of contact between the surfaces; (3) kinetic friction is nearly independent of the relative speed of sliding, for ordinary speeds.",
    example: `f = mu . N

A 10 kg block (N = mg = 100 N, g = 10 m/s^2) with mu(k) = 0.3:
Kinetic friction f(k) = 0.3 x 100 N = 30 N
(same whether the block's contact face is small or large)` },
  { section: "Friction", title: "Angle of Friction and Angle of Repose",
    explanation: "The angle of friction (lambda) is the angle that the resultant of the limiting friction force and the normal reaction makes with the normal reaction itself; tan(lambda) = f(max)/N = mu(s). The angle of repose is the maximum angle of an inclined surface at which a body placed on it remains just on the verge of sliding under gravity alone, with no other applied force -- numerically, the angle of repose equals the angle of friction, since at that angle mg.sin(theta) just equals the maximum available static friction mg.mu(s).cos(theta), giving tan(theta) = mu(s).",
    example: `tan(lambda) = mu(s)                (angle of friction)
tan(theta_repose) = mu(s)          (angle of repose -- same value)

If mu(s) = 0.5:
theta_repose = tan^-1(0.5) ~ 26.6 degrees` },
  { section: "Friction", title: "Motion on an Inclined Plane with Friction",
    explanation: "For a block of mass m sliding down a rough incline of angle theta, resolve weight into mg.sin(theta) (along the incline, down-slope) and mg.cos(theta) (into the incline). Normal reaction N = mg.cos(theta), so kinetic friction f = mu(k).N = mu(k).mg.cos(theta) opposes the sliding (acts up the slope). Net force along the incline = mg.sin(theta) - f, giving acceleration a = g(sin(theta) - mu(k).cos(theta)). The block slides at all only if tan(theta) > mu(s) -- otherwise static friction holds it in place.",
    example: `Block: m = 5 kg, theta = 37 deg (sin37=0.6, cos37=0.8),
mu(k) = 0.25, g = 10 m/s^2

Normal reaction:  N = mg.cos(theta) = 5x10x0.8 = 40 N
Friction force:   f = mu(k).N = 0.25 x 40 = 10 N
Driving force:    mg.sin(theta) = 5x10x0.6 = 30 N
Net force along incline = 30 - 10 = 20 N
Acceleration a = 20 N / 5 kg = 4 m/s^2  (down the incline)` },

  // ── Circular Motion Dynamics ─────────────────────────────────────────────────
  { section: "Circular Motion Dynamics", title: "Centripetal Force in Circular Motion",
    explanation: "A body moving in a circle at constant speed still ACCELERATES, because its velocity direction keeps changing -- this centripetal acceleration points toward the centre and has magnitude a(c) = v^2/r = omega^2.r. By Newton's second law, this requires a net force toward the centre, the centripetal force, F(c) = m.v^2/r. Centripetal force is NOT a new, separate kind of force -- it is simply the net effect of the real forces present (tension, gravity, normal reaction, friction, etc.) that happens to point toward the centre.",
    example: `F(c) = m.v^2/r

Car of mass m = 1000 kg rounds a circle of radius r = 50 m at a
constant speed v = 10 m/s:
F(c) = (1000 x 10^2) / 50 = 100000/50 = 2000 N
(supplied here by friction between tyres and road)` },
  { section: "Circular Motion Dynamics", title: "Banking of Roads",
    explanation: "On a flat road, the centripetal force for a turning vehicle must come entirely from friction, which is unreliable at high speed. Banking the road at an angle theta tilts the normal reaction so its horizontal component itself supplies part (or all) of the centripetal force. For the IDEAL (design) speed, where friction is not needed at all, resolving forces gives N.cos(theta) = mg (vertical) and N.sin(theta) = mv^2/r (horizontal, centripetal); dividing these gives the banking relation tan(theta) = v^2/(r.g).",
    example: `N cos(theta) = mg              (vertical equilibrium)
N sin(theta) = m v^2 / r        (horizontal = centripetal force)
Dividing:  tan(theta) = v^2 / (r g)

Curve radius r = 20 m, design speed v = 10 m/s, g = 10 m/s^2:
tan(theta) = 10^2 / (20 x 10) = 100/200 = 0.5
theta = tan^-1(0.5) ~ 26.6 degrees` },
  { section: "Circular Motion Dynamics", title: "Conical Pendulum",
    explanation: "A conical pendulum is a bob attached to a string of length L, whirled so that it moves in a horizontal circle of radius r = L.sin(theta) while the string sweeps out a cone at constant angle theta from the vertical. Resolving tension: T.cos(theta) = mg (vertical) and T.sin(theta) = mv^2/r (horizontal, centripetal). Combining these with r = L.sin(theta) gives the time period T(period) = 2.pi.sqrt(L.cos(theta)/g) -- independent of the bob's mass.",
    example: `T cos(theta) = mg
T sin(theta) = m v^2 / r ,     r = L sin(theta)
=> Time period:  T(period) = 2 pi sqrt( L cos(theta) / g )

L = 2 m, theta = 60 deg (cos60 = 0.5), g = 10 m/s^2:
T(period) = 2 pi sqrt(2 x 0.5 / 10) = 2 pi sqrt(0.1)
          = 2 pi x 0.316 ~ 1.99 s  (~ 2.0 s)` },
];

const S = {
  page:  { minHeight:"100vh", background:"linear-gradient(135deg,#0f172a,#1a1a2e)", fontFamily:"'Segoe UI',sans-serif", color:"#e2e8f0", padding:"18px 16px 80px",
    userSelect:"none", WebkitUserSelect:"none", MozUserSelect:"none", msUserSelect:"none" },
  back:  { color:"#94a3b8", fontSize:13, textDecoration:"none" },
  card:  { background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.11)", borderRadius:14, padding:"18px", marginBottom:16 },
  chip:  (active, color) => ({
    background: active ? color : "rgba(255,255,255,0.08)",
    color: active ? "#fff" : "#94a3b8",
    border:"none", borderRadius:20, padding:"6px 14px", fontSize:12.5, fontWeight:700, cursor:"pointer", marginRight:8, marginBottom:8,
  }),
  tag: (color) => ({ background:color, color:"#fff", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, display:"inline-block" }),
  pre: { background:"#0b1120", border:"1px solid rgba(255,255,255,0.08)", borderRadius:10, padding:"12px 14px", fontSize:12.5, lineHeight:1.6, color:"#a5f3b8", overflowX:"auto", fontFamily:"'Fira Code','Consolas',monospace", whiteSpace:"pre" },
  option: (bg) => ({ display:"block", width:"100%", textAlign:"left", background:bg, border:"1px solid rgba(255,255,255,0.1)", borderRadius:8, padding:"9px 12px", color:"#e2e8f0", fontSize:13, marginBottom:7, cursor:"pointer" }),
  iconBtn: (bg) => ({ background:bg, border:"none", borderRadius:8, width:30, height:30, fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", flex:"none" }),
};

// ── One interactive objective question — click an option to check it ──────────
function QuizQuestion({ item, index, onAnswer }) {
  const [selected, setSelected] = useState(null);
  const answered = selected !== null;

  const pick = (i) => {
    if (answered) return;
    setSelected(i);
    onAnswer && onAnswer(i === item.correct);
  };

  return (
    <div style={S.card}>
      <span style={S.tag(SECTION_COLORS[item.section])}>{item.section}</span>
      <span style={{ fontSize:11, color:"#64748b", marginLeft:8 }}>{item.topic}</span>
      <div style={{ fontWeight:700, fontSize:14, color:"#fff", marginTop:8, marginBottom:10 }}>{index + 1}. {item.q}</div>
      {item.options.map((opt, i) => {
        let bg = "rgba(255,255,255,0.05)";
        if (answered) {
          if (i === item.correct) bg = "rgba(34,197,94,0.28)";
          else if (i === selected) bg = "rgba(239,68,68,0.28)";
        }
        return (
          <button key={i} style={S.option(bg)} onClick={() => pick(i)} disabled={answered}>
            {String.fromCharCode(65 + i)}. {opt}
          </button>
        );
      })}
      {answered && (
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:8, lineHeight:1.6 }}>
          {selected === item.correct ? "✅ Correct — " : "❌ Not quite — "}{item.explain}
        </div>
      )}
    </div>
  );
}

export default function NeetPhysicsCh4Page() {
  const [mode, setMode] = useState("guide"); // "guide" | "practice"
  const [activeSection, setActiveSection] = useState("All");
  const [score, setScore] = useState({ correct: 0, answered: 0 });

  // ── Bookmarks — same 🔖 pattern used elsewhere on the site, persisted locally ──
  const [bookmarks, setBookmarks] = useState(() => {
    try {
      const raw = window.localStorage.getItem("neetPhysicsCh4Bookmarks");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });
  const [showBookmarkedOnly, setShowBookmarkedOnly] = useState(false);

  useEffect(() => {
    try { window.localStorage.setItem("neetPhysicsCh4Bookmarks", JSON.stringify(bookmarks)); } catch { /* ignore */ }
  }, [bookmarks]);

  const toggleBookmark = (title) => {
    setBookmarks(prev => prev.includes(title) ? prev.filter(t => t !== title) : [...prev, title]);
  };

  // ── Read aloud — same speechSynthesis pattern used on Reading Notes / TNPSC ──
  const [speakingIdx, setSpeakingIdx] = useState(null);

  useEffect(() => () => { window.speechSynthesis && window.speechSynthesis.cancel(); }, []);

  const speakConcept = (c, idx) => {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    if (speakingIdx === idx) { setSpeakingIdx(null); return; }
    const u = new window.SpeechSynthesisUtterance(`${c.title}. ${c.explanation}`);
    u.rate = 0.95;
    u.onend = () => setSpeakingIdx(null);
    u.onerror = () => setSpeakingIdx(null);
    window.speechSynthesis.speak(u);
    setSpeakingIdx(idx);
  };

  // ── Share — native share sheet, falling back to WhatsApp like the other pages ──
  const shareConcept = (c, idx) => {
    const url = `${window.location.origin}${window.location.pathname}#concept-${idx}`;
    const text = `🧪 NEET Physics — ${c.title}\n\n${c.explanation}\n\n${url}`;
    if (navigator.share) {
      navigator.share({ title: `NEET Physics — ${c.title}`, text, url }).catch(() => {});
    } else {
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
    }
  };

  const filtered = useMemo(() => {
    let list = activeSection === "All" ? CONCEPTS : CONCEPTS.filter(c => c.section === activeSection);
    if (showBookmarkedOnly) list = list.filter(c => bookmarks.includes(c.title));
    return list;
  }, [activeSection, showBookmarkedOnly, bookmarks]);

  const filteredQuestions = useMemo(() => (
    activeSection === "All" ? NEET_PHYSICS_CH4_QUESTIONS : NEET_PHYSICS_CH4_QUESTIONS.filter(q => q.section === activeSection)
  ), [activeSection]);

  const handleModeChange = (m) => {
    setMode(m);
    setScore({ correct: 0, answered: 0 });
  };

  const handleAnswer = (isCorrect) => {
    setScore(s => ({ correct: s.correct + (isCorrect ? 1 : 0), answered: s.answered + 1 }));
  };

  return (
    <div
      style={S.page}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onContextMenu={(e) => e.preventDefault()}
    >
      <Link to="/neet-physics" style={S.back}>← Back to NEET Physics Menu</Link>

      <div style={{ textAlign:"center", padding:"14px 0 10px" }}>
        <div style={{ fontSize:26, fontWeight:800, color:"#fff" }}>🧪 NEET Physics — Chapter 4</div>
        <div style={{ fontSize:15, fontWeight:700, color:"#fbbf24", marginTop:4 }}>Newton's Laws of Motion</div>
        <div style={{ fontSize:12.5, color:"#94a3b8", marginTop:6 }}>
          NCERT Class 11, Laws of Motion · {CONCEPTS.length} concepts explained · {NEET_PHYSICS_CH4_QUESTIONS.length} practice questions · NOT YET human-reviewed, verify against NCERT before exam use
        </div>
      </div>

      {/* Guide / Practice mode toggle */}
      <div style={{ textAlign:"center", margin:"14px 0 8px" }}>
        <button style={S.chip(mode==="guide", "#0f766e")} onClick={() => handleModeChange("guide")}>📘 Concept Guide</button>
        <button style={S.chip(mode==="practice", "#b45309")} onClick={() => handleModeChange("practice")}>🎯 Practice Questions ({NEET_PHYSICS_CH4_QUESTIONS.length})</button>
      </div>

      {/* Section filter chips */}
      <div style={{ textAlign:"center", margin:"6px 0 18px" }}>
        <button style={S.chip(activeSection==="All", "#334155")} onClick={() => setActiveSection("All")}>All</button>
        {SECTIONS.map(s => (
          <button key={s} style={S.chip(activeSection===s, SECTION_COLORS[s])} onClick={() => setActiveSection(s)}>{s}</button>
        ))}
      </div>

      {mode === "practice" ? (
        <>
          {score.answered > 0 && (
            <div style={{ textAlign:"center", fontSize:13, fontWeight:700, color:"#fbbf24", marginBottom:14 }}>
              Score: {score.correct} / {score.answered} answered correctly
            </div>
          )}
          {filteredQuestions.map((item, i) => (
            <QuizQuestion key={i} item={item} index={i} onAnswer={handleAnswer} />
          ))}
        </>
      ) : (
      <>
      {/* ── Bookmark filter ── */}
      <div style={{ textAlign:"center", margin:"0 0 14px" }}>
        <button style={S.chip(showBookmarkedOnly, "#db2777")} onClick={() => setShowBookmarkedOnly(v => !v)}>
          🔖 {showBookmarkedOnly ? "Showing Bookmarked" : "Bookmarked"} ({bookmarks.length})
        </button>
      </div>

      {/* ── Quick reference table ── */}
      <div style={S.card}>
        <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginBottom:12 }}>📋 Quick Reference Table</div>
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
            <thead>
              <tr style={{ borderBottom:"2px solid rgba(255,255,255,0.15)" }}>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>#</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Section</th>
                <th style={{ textAlign:"left", padding:"6px 8px", color:"#94a3b8" }}>Topic</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c, i) => (
                <tr key={i} style={{ borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                  <td style={{ padding:"7px 8px", color:"#64748b" }}>{i + 1}</td>
                  <td style={{ padding:"7px 8px" }}><span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span></td>
                  <td style={{ padding:"7px 8px" }}>
                    <a
                      href={`#concept-${i}`}
                      style={{ color:"#38bdf8", textDecoration:"underline", fontWeight:600 }}
                      onMouseEnter={(e) => { e.currentTarget.style.color = "#7dd3fc"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.color = "#38bdf8"; }}
                    >
                      {c.title}
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Detailed concept cards ── */}
      {filtered.map((c, i) => {
        const isBookmarked = bookmarks.includes(c.title);
        const isSpeaking = speakingIdx === i;
        return (
        <div key={i} id={`concept-${i}`} style={S.card}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:8, flexWrap:"wrap" }}>
            <span style={S.tag(SECTION_COLORS[c.section])}>{c.section}</span>
            <div style={{ display:"flex", gap:6 }}>
              <button
                title={isSpeaking ? "Stop reading" : "Read aloud"}
                onClick={() => speakConcept(c, i)}
                style={S.iconBtn(isSpeaking ? "#2563eb" : "rgba(37,99,235,0.22)")}
              >{isSpeaking ? "⏹" : "🔊"}</button>
              <button
                title={isBookmarked ? "Remove bookmark" : "Bookmark"}
                onClick={() => toggleBookmark(c.title)}
                style={S.iconBtn(isBookmarked ? "#db2777" : "rgba(219,39,119,0.22)")}
              >{isBookmarked ? "🔖" : "📌"}</button>
              <button
                title="Share"
                onClick={() => shareConcept(c, i)}
                style={S.iconBtn("rgba(255,255,255,0.12)")}
              >↗</button>
            </div>
          </div>
          <div style={{ fontWeight:800, fontSize:16, color:"#fff", marginTop:8 }}>{i + 1}. {c.title}</div>
          <div style={{ fontSize:13.5, color:"#cbd5e1", lineHeight:1.7, marginTop:8 }}>{c.explanation}</div>
          <div style={{ fontSize:11, fontWeight:700, color:"#94a3b8", marginTop:12, marginBottom:6, letterSpacing:0.4 }}>EXAMPLE / WORKED CALCULATION</div>
          <pre style={S.pre}><code>{c.example}</code></pre>
        </div>
        );
      })}
      </>
      )}
    </div>
  );
}
